import React, { useState, useEffect, useContext } from 'react'
import {
  AuthState,
  AuthContext,
  UserType,
  CustomClaims
} from '../../../contexts/authContext'
import { User } from 'firebase'
import { firebaseAuth } from '../../../services/firebase'
import { StoreKey } from '../../../config/enums'
import { MigrantContext } from '../../../contexts/migrantContext'
import { VendorContext } from '../../../contexts/vendorContext'

const AuthContextProvider: React.FC = ({ children }) => {
  const migrantContext = useContext(MigrantContext)
  const vendorContext = useContext(VendorContext)

  const [authState, setAuthState] = useState<AuthState>(() => {
    const data = localStorage.getItem(StoreKey.authState)
    if (data === null) {
      return AuthState.signedOut
    }
    return JSON.parse(data) as AuthState
  })

  const [user, setUser] = useState<User | null>(() => {
    return firebaseAuth().currentUser
  })

  const [userType, setUserType] = useState<UserType>(() => {
    const data = localStorage.getItem(StoreKey.userType)
    if (data === null) {
      return UserType.migrant
    }
    return JSON.parse(data) as UserType
  })

  const [customClaims, setCustomClaims] = useState<CustomClaims | null>(() => {
    const data = sessionStorage.getItem(StoreKey.customClaims)
    if (data !== null) {
      return JSON.parse(data) as CustomClaims
    }
    return null
  })

  const [redirectTo, setRedirectTo] = useState<string | null>(null)

  useEffect(() => {
    let isPreviouslyLoggedIn = false
    firebaseAuth().onAuthStateChanged(fbUser => {
      if (fbUser && fbUser.uid !== user?.uid) {
        setAuthState(AuthState.signedIn)
        setUser(fbUser)
        isPreviouslyLoggedIn = true
      } else if (!fbUser && authState !== AuthState.signingIn) {
        // Reset cart on migrant signout
        if (isPreviouslyLoggedIn) {
          migrantContext?.resetCachedCart()
        }

        isPreviouslyLoggedIn = false
        setAuthState(AuthState.signedOut)
        setUser(null)
        setCustomClaims(null)
        migrantContext?.setProfile(null)
        vendorContext?.setProfile(null)
        vendorContext?.setCompanyProfile(null)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    localStorage.setItem(StoreKey.authState, JSON.stringify(authState))
  }, [authState])

  useEffect(() => {
    localStorage.setItem(StoreKey.userType, JSON.stringify(userType))
  }, [userType])

  useEffect(() => {
    sessionStorage.setItem(StoreKey.customClaims, JSON.stringify(customClaims))
  }, [customClaims])

  return (
    <AuthContext.Provider
      value={{
        authState,
        user,
        userType,
        setUserType,
        customClaims: customClaims,
        setCustomClaims: setCustomClaims,
        redirectTo,
        setRedirectTo
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContextProvider
